import "./annaCallRobotApiTypes";

import axios, { type AxiosError } from "axios";

import { API_BASE_URL, PRODUCTION_PREFIX } from "../config";
import { PATHS } from "../config/paths";

export function clearAuthCredentials(): void {
  try {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_username");
  } catch {
    /* ignore quota / private mode */
  }
}

function getRouterLoginHref(): string {
  const path = window.location.pathname || "";
  const basename = path.startsWith(PRODUCTION_PREFIX) ? PRODUCTION_PREFIX : "";
  return `${basename}${PATHS.LOGIN}`;
}

function normalizePathname(pathname: string): string {
  const trimmed = (pathname || "/").replace(/\/+$/, "");
  return trimmed || "/";
}

function isLoginRoutePathname(pathname: string): boolean {
  const current = normalizePathname(pathname);
  const loginHref = normalizePathname(getRouterLoginHref());
  if (current === loginHref) {
    return true;
  }
  return current.endsWith(normalizePathname(PATHS.LOGIN));
}

let sessionExpiredHandling = false;

function redirectSessionExpired(): void {
  if (sessionExpiredHandling) {
    return;
  }
  sessionExpiredHandling = true;
  clearAuthCredentials();
  if (isLoginRoutePathname(window.location.pathname || "")) {
    return;
  }
  window.location.replace(getRouterLoginHref());
}

/** Axios instance for Anna Call Robot backend (`API_BASE_URL`). Handles Bearer auth and 401 session expiry. */
export const annaCallRobotApi = axios.create({
  baseURL: API_BASE_URL,
});

annaCallRobotApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

annaCallRobotApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const config = error.config;
    if (status !== 401 || !config || config.skip401Redirect) {
      return Promise.reject(error);
    }
    const url = config.url || "";
    const method = (config.method || "get").toLowerCase();
    const isLoginAttempt =
      method === "post" && (url === "/login" || url.endsWith("/login"));
    if (!isLoginAttempt) {
      redirectSessionExpired();
    }
    return Promise.reject(error);
  },
);
